import { useEffect, useCallback, useRef } from 'react'
import { storeLastActivity } from '../helpers/session_helper'

// Throttle function to limit how often we update activity
const throttle = (func, limit) => {
    let inThrottle
    return function() {
        const args = arguments
        const context = this
        if (!inThrottle) {
            func.apply(context, args)
            inThrottle = true
            setTimeout(() => inThrottle = false, limit)
        }
    }
}

const useUserActivity = (throttleMs = 5000) => { // Default 5 seconds throttle
    const throttledUpdateRef = useRef(null)

    // Update activity timestamp in localStorage
    const updateActivity = useCallback(() => {
        storeLastActivity()
    }, [])

    // Create throttled version of updateActivity
    useEffect(() => {
        throttledUpdateRef.current = throttle(updateActivity, throttleMs)
    }, [updateActivity, throttleMs])

    useEffect(() => {
        const events = [
            'mousemove',
            'mousedown', 
            'click',
            'keydown',
            'keypress',
            'scroll',
            'wheel',
            'focus'
        ]

        const handleActivity = () => {
            if (throttledUpdateRef.current) {
                throttledUpdateRef.current()
            }
        }

        // Add event listeners to document to catch all interactions
        events.forEach(event => {
            document.addEventListener(event, handleActivity, { passive: true })
        })

        // Handle page visibility changes
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                // User came back to tab
                updateActivity()
            }
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)

        // Store initial activity on mount
        updateActivity()

        // Cleanup
        return () => {
            events.forEach(event => {
                document.removeEventListener(event, handleActivity)
            })
            document.removeEventListener('visibilitychange', handleVisibilityChange)
        }
    }, [updateActivity])
}

export default useUserActivity